<template>
    <v-form @submit.prevent="addEditPlan" ref="form">
        <v-card dense style="border-radius:4px;">
            <v-toolbar flat height="45px">
                <span class="font-weight-bold">Workshift Plan</span>
                <v-spacer></v-spacer>
                <v-btn icon @click.native="closeDialog">
                    <v-icon >close</v-icon>
                </v-btn>
            </v-toolbar>
            <v-card-text dense>
                <p v-if="error" style="color:red;">{{ errorMessage }}</p>
                <v-layout v-if="user_data" row wrap class="data-form">
                    <v-flex xs12 sm12> 
                        <v-chip>
                            <v-avatar>
                                <v-img ref="avatar" :src="awsS3Helper.getAvatarUrl(user_data.userId)"></v-img>
                            </v-avatar>
                            {{user_data.firstName}} {{user_data.lastName}}
                        </v-chip>
                    </v-flex>
                </v-layout> 
                <v-layout row wrap class="data-form-select">
                    <v-flex xs12 sm12> 
                        <v-autocomplete
                            label="Workshift"
                            v-model="workShiftId"
                            :disabled="disabled"
                            :items="workshifts_data"
                            :loading="loading"
                            :rules="[rules.required]"
                            chips
                            color="primary"
                            item-text="name"
                            item-value="workShiftId"
                            @change="changeColor" 
                            >
                            <template v-slot:selection="data">
                                <v-chip :selected="data.selected" class="chip--select-multi" :color="color">
                                    {{ data.item.name }}
                                </v-chip>
                            </template>
                            <template v-slot:item="data">
                                <template v-if="typeof data.item !== 'object'">
                                    <v-list-tile-content v-text="data.item"></v-list-tile-content>
                                </template>
                                <template v-else>
                                    <v-list-tile-content>
                                        <v-list-tile-title v-html="data.item.name"></v-list-tile-title>
                                        <v-list-tile-sub-title v-html="data.item.group"></v-list-tile-sub-title>
                                    </v-list-tile-content>
                                </template>
                            </template>
                        </v-autocomplete>
                    </v-flex>
                </v-layout> 
                <v-layout row wrap class="data-form">
                    <v-flex xs12 sm6 style="padding-right:15px;"> 
                        <v-menu v-model="menuStartDate" :close-on-content-click="false" :nudge-right="40"
                            lazy transition="scale-transition" offset-y full-width min-width="290px">
                            <template v-slot:activator="{ on }">
                                <v-text-field label="Start Date" v-model="startDate" readonly v-on="on" :rules="[rules.required]" :disabled="disabled"></v-text-field>
                            </template>
                            <v-date-picker no-title v-model="startDate" @input="updateData" :allowed-dates="allowedStartDates"></v-date-picker>
                        </v-menu>
                    </v-flex>   
                    <v-flex xs12 sm6 style="padding-left:15px;"> 
                        <v-menu v-model="menuEndDate" :close-on-content-click="false" :nudge-right="40"
                            lazy transition="scale-transition" offset-y full-width min-width="290px">
                            <template v-slot:activator="{ on }">
                                <v-text-field label="End Date" v-model="endDate" readonly v-on="on" :rules="[rules.required]" :disabled="disabled"></v-text-field>
                            </template>
                            <v-date-picker no-title v-model="endDate" @input="menuEndDate=false" :allowed-dates="allowedEndDates"></v-date-picker>
                        </v-menu>
                    </v-flex>   
                </v-layout>
                <v-layout row wrap class="data-form">
                    <v-flex xs12 sm12 >
                        <div style="margin:0px 0px 15px 0px;">
                            <v-switch
                            v-model="includeWeekends"
                            label="Include weekends"
                            color="primary"
                            persistent-hint
                            :hint="includeWeekends?'Will include weekends if within selected date range':'Will not include weekends if within selected date range'"
                            ></v-switch> 
                        </div>
                    </v-flex>
                </v-layout>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions style="height:45px">
                <v-spacer></v-spacer>                
                <v-btn v-if="workShiftsPlanId&&!hasSelectedNewWorkshift" color="red" :loading="loading" @click="showDelete" dark>Delete</v-btn>
                <v-btn color="primary" type="submit" :disabled="!hasFormChanges||error" :loading="loading" style="margin-right:25px;">Save</v-btn>
            </v-card-actions>
        </v-card>
        <v-dialog v-model="dialogDelete" width="500">
            <delete ref="refDeleteComponent" :dialogDelete.sync="dialogDelete" @okDelete="deleteWorkshiftPlan"></delete>
        </v-dialog>
    </v-form>
</template>
<script>

import workshiftsplanApi from '@/api/workshiftsplan'
import commonfn from '@/common/functions'
import awsS3Helper from '@/helpers/aws-s3'

export default {
    components: {
        'delete': () => import('@/components/BasicPopups/Delete'),
    },
    data() {
        return {
            error: false,
            errorMessage: '', 
            disabled: false,
            rules: {
                required: value => !!value || 'Required.',
                min: v => v.length >= 8 || 'Min 8 characters'
            },
            loading: false,
            loading_delete: false,
            dialogDelete: false,
            user_data: null,
            workshifts_data: [],
            teamId: null,
            workShiftsPlanId: null,
            workShiftId: null,
            originalWorkShiftId: null,
            includeWeekends: false,
            overwriteExisting: true,    //when adding/updating individual workshift plan, it's always overwrrite if exist
            menuStartDate: false,
            startDate: null,
            menuEndDate: false,
            endDate: null,
            color: null,
            pageKey: null
        }
    },
    methods: {
        showDelete() {
            this.$refs.refDeleteComponent.setMessage("Delete this workshift plan?")
            this.dialogDelete = true
        },
        async deleteWorkshiftPlan() {
            try {
                this.loading_delete = true
                const response = await workshiftsplanApi.delete(this.workShiftsPlanId)
                if (response && response.status == 200) {
                    this.$emit('send',null)
                    this.closeDialog()
                }
                this.loading_delete = false
            }catch(error){
                this.error = true
                this.errorMessage = error
            }
        },
        async addEditPlan() {
            try {
                if (this.$refs.form.validate()) {
                    this.loading = true
                    this.disabled = true
                    const data = {
                        teamId: this.teamId,
                        workShiftId: this.workShiftId,
                        startDate: this.startDate,
                        endDate: this.endDate,
                        userId: this.user_data.userId,
                        fiscalYear: this.user_data.fiscalYear,
                        weekNumber: this.user_data.weekNumber,
                        includeWeekends: this.includeWeekends,
                        overwriteExisting: this.overwriteExisting,
                    }
                    const response = await workshiftsplanApi.update(data)
                    if (response && response.status == 200) {
                        this.$emit('send',null)
                        this.closeDialog()
                    }
                    this.disabled = false
                    this.loading = false
                }
            }catch(error){
                this.error = true
                this.errorMessage = error
            }
        },
        allowedStartDates(val) {
            //allow only current or future dates in start date
            const currentDate = new Date()
            return this.$moment(val).format('YYYY-MM-DD') >= this.$moment(currentDate).format('YYYY-MM-DD')
        },
        allowedEndDates(val) {
            //allow only dates equal or greater than start date
            return this.$moment(val).format('YYYY-MM-DD') >= this.$moment(this.startDate).format('YYYY-MM-DD')
        },
        changeColor(val) {
            const data = this.workshifts_data.filter(x => x.workShiftId == val)
            if (data.length) {
                this.color = data[0].color
            } else {
                this.color = 'transparent'
            }
        },
        updateData() {
            this.menuStartDate=false
            if (this.$moment(this.startDate).format('YYYY-MM-DD') > this.$moment(this.endDate).format('YYYY-MM-DD')) {
                this.endDate = this.startDate
            }
        },
        async setUserData(data) {
            this.user_data = data
            this.pageKey = data.pageKey
            if (this.workshifts_data.length == 0) {
                await this.getWorkShifts()                
            }
            this.changeColor(data.workShiftId)
            this.workShiftsPlanId = data.workShiftsPlanId
            this.workShiftId = data.workShiftId
            this.originalWorkShiftId = data.workShiftId
            this.teamId = data.teamId
            this.startDate = this.$moment(data.startDate).format('YYYY-MM-DD')
            this.endDate = this.$moment(data.endDate).format('YYYY-MM-DD')
        },
        async getWorkShifts() {
            try {
                this.loading = true
                const response = await workshiftsplanApi.getWorkShifts() 
                if (response && response.status == 200) {
                    this.workshifts_data = response.data.items
                }
                this.loading = false
            }catch(error) {
                this.error = true
                this.errorMessage = error
            }
        },
        closeDialog() {
            this.$emit('update:dialogWorkShiftPlan', false)
        }
    },
    created() {
        this.commonfn = commonfn
        this.awsS3Helper = awsS3Helper
    },
    computed: {
        hasFormChanges() {
            return (
                this.workShiftId != this.user_data.workShiftId ||
                this.$moment(this.startDate).format('YYYY-MM-DD') != this.$moment(this.user_data.startDate).format('YYYY-MM-DD') ||
                this.$moment(this.endDate).format('YYYY-MM-DD') != this.$moment(this.user_data.endDate).format('YYYY-MM-DD')
            )
        },
        hasSelectedNewWorkshift() {
            return this.originalWorkShiftId != this.workShiftId
        }
    },
}
</script>
<style scoped>
.data-form {
    padding: 0px 25px 0px 25px;
}
.data-form-select {
    margin: 10px 25px 0px 25px;
}
</style>
